import { DateTime } from 'luxon';
import { CONFIG, ENV } from '@components/Environment.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
/*
* npm install luxon
* npm i --save-dev @types/luxon
* */
/**
 * DateTimeManager is a wrapper class for Luxon so that we can easily replace Luxon later if we decide to. (ie. maybe when temporal is production ready)
 *
 * @example
 * // Get the current datetime as a string in the default format
 * const currentDateString = DateTimeManager.now<string>();
 * console.log(currentDateString);
 *
 * // Get the current datetime as a string in a specific timezone
 * const formattedNow = DateTimeManager.now<string>('EST', 'string');
 * console.log(formattedNow);
 *
 *
 *
 * // Add 5 days to a Luxon DateTime object
 * const initialDateTime = DateTimeManager.fromISO('2024-11-15T12:00:00');
 * const newDateTime = DateTimeManager.dateAdd<DateTime>(initialDateTime, 'days', 5);
 * console.log(newDateTime.toISO()); // Outputs: '2024-11-20T12:00:00.000Z'
 *
 * // Add 3 days to a string date
 * const initialDateString = '2024-11-10';
 * const newDateString = DateTimeManager.dateAdd<string>(initialDateString, 'days', 3);
 * console.log(newDateString); // Outputs: '2024-11-13'
 *
 * // Add 2 days to a JavaScript Date object
 * const jsDate = new Date('2024-11-05T10:00:00');
 * const newJsDate = DateTimeManager.dateAdd<Date>(jsDate, 'days', 2);
 * console.log(newJsDate); // Outputs: JavaScript Date object representing 2024-11-07
 *
 *
 *
 * // Format a Luxon DateTime object
 * const dateTime = DateTimeManager.fromISO('2024-11-15T10:00:00');
 * const formattedDateTime = DateTimeManager.formatDate(dateTime, 'yyyy-MM-dd HH:mm');
 * console.log(formattedDateTime); // Outputs: '2024-11-15 10:00'
 *
 * // Format a JavaScript Date object
 * const jsDateToFormat = new Date('2024-11-12T14:30:00');
 * const formattedJsDate = DateTimeManager.formatDate(jsDateToFormat, 'yyyy-MM-dd HH:mm:ss');
 * console.log(formattedJsDate); // Outputs: '2024-11-12 14:30:00'
 *
 * // Format a string date
 * const stringDate = '2024-11-15';
 * const formattedStringDate = DateTimeManager.formatDate(stringDate, 'yyyy-MM-dd');
 * console.log(formattedStringDate); // Outputs: '2024-11-15'
 *
 *
 * // Convert a date between timezones and format it
 * const date = '2024-11-15 10:00:00.000';
 * const formattedWithTimezone = DateTimeManager.formatDateTime(date, 'yyyy-MM-dd HH:mm', 'EST', 'CST');
 * console.log(formattedWithTimezone); // Outputs: the formatted date-time string in CST time
 */
export class DateTimeManager {
    // due to our DATEs being stored in DATETIME columns usually, and Luxon being very specific about the format you tell it, we need to try both formats
    // for situations where a DATE loads (starts as a full datetime string) , then gets edited and now is a short string.
    // we cant just assume it will always be the long string format because someone might actually use an actual DATE column in a database table. 
    static parseDateTime(dateString, sourceTZ = 'SYSTEM', displayFormats = false) {
        // the possible formats
        const formats = ['yyyy-MM-dd HH:mm:ss.SSS', 'yyyy-MM-dd HH:mm:ss', 'yyyy-MM-dd HH:mm', 'yyyy-MM-dd', 'HH:mm'];
        const tz = this.q360TimezoneToIANA(sourceTZ);
        if (displayFormats) {
            formats.push(ENV('datetimeFormat'));
            if (ENV('datetimeFormat').indexOf('a') > -1) {
                formats.push(ENV('datetimeFormat').replace(' a', 'a'));
            }
            formats.push(ENV('dateFormat'));
            formats.push('hh:mm a');
            formats.push('hh:mma');
        }
        // Try to parse with each format
        for (const format of formats) {
            const dateTime = DateTime.fromFormat(dateString, format, { zone: tz });
            if (dateTime.isValid) {
                return dateTime;
            }
        }
        // If none of the formats work, return an invalid DateTime
        return DateTime.invalid('Invalid input format');
    }
    static getMonthName(date) {
        const dateTime = this.toDateTime(date);
        const monthNumber = dateTime.toFormat('M');
        const monthCaptionKey = `system_month${monthNumber}_caption`;
        return DIContainer.getInstance('CaptionManager').getCaption(monthCaptionKey);
    }
    static getDayName(date) {
        const dateTime = this.toDateTime(date);
        const monthCaptionKey = `system_day${(dateTime.weekday % 7) + 1}_caption`;
        return DIContainer.getInstance('CaptionManager').getCaption(monthCaptionKey);
    }
    static dateAdd(date, unit, amount) {
        const dateTime = this.toDateTime(date).plus({ [unit]: amount });
        return this.convertBackToOriginalType(dateTime, date);
    }
    static dateDiff(start, end, unit, options) {
        if (options && options.start_tz !== options.end_tz) {
            // ensure both tz values are set then set the end date tz to match the start
            options.start_tz = !options.start_tz ? CONFIG('SERVERTIMEZONE', 1) : options.start_tz;
            options.end_tz = !options.end_tz ? CONFIG('SERVERTIMEZONE', 1) : options.end_tz;
            end = this.convertTimezone(end, options.end_tz, options.start_tz);
        }
        if (!(start instanceof DateTime)) {
            start = this.toDateTime(start);
        }
        if (!(end instanceof DateTime)) {
            end = this.toDateTime(end);
        }
        const diffs = end.diff(start, unit);
        return diffs[unit];
    }
    /*
    * This utility will normalize dates as string, Luxon DateTime, or JS Date into Luxon DateTime format.
    *
    * */
    static toDateTime(date, sourceTZ = 'SYSTEM') {
        if (date instanceof DateTime) {
            const ianaTimezone = this.q360TimezoneToIANA(sourceTZ);
            return date.setZone(ianaTimezone, { keepLocalTime: true });
        }
        else if (typeof date === 'string') {
            return this.parseDateTime(date, sourceTZ);
        }
        else {
            const ianaTimezone = this.q360TimezoneToIANA(sourceTZ);
            return DateTime.fromJSDate(date).setZone(ianaTimezone, { keepLocalTime: true });
        }
    }
    /*
    * Internal Utility to facilitate passing any date type into this class, and being given back the same time.
    * */
    static convertBackToOriginalType(dateTime, original) {
        if (original === 'DateTime' || original instanceof DateTime) {
            return dateTime;
        }
        else if (original === 'string' || (typeof original === 'string' && original !== 'Date')) {
            return dateTime.toFormat(this.dateTimeFormat);
        }
        else {
            // extract datetime parts from the timezone our dateTime is set to, JS Dates will always revert to local timezone so we cant pass it an ISO string or use DateTime.toJSDate or the timestamp gets converted. 
            const year = dateTime.year;
            const month = dateTime.month - 1; // JavaScript months are 0-indexed
            const day = dateTime.day;
            const hour = dateTime.hour;
            const minute = dateTime.minute;
            const second = dateTime.second;
            const millisecond = dateTime.millisecond;
            return new Date(year, month, day, hour, minute, second, millisecond);
        }
    }
    /**
    * Utility to convert a date string of a known format to Luxon DateTime
    * */
    static fromFormat(dateString, format = ENV('datetimeStorageFormat')) {
        return DateTime.fromFormat(dateString, format);
    }
    /**
    * Utility to convert a date string known to be in ISO format to Luxon Datetime
    * */
    static fromISO(isoString) {
        return DateTime.fromISO(isoString);
    }
    /**
    * Utility to get current client time as a Luxon DateTime object.
    * */
    static now(timezone, returnType = 'DateTime') {
        let now = DateTime.now();
        if (timezone) {
            const ianaTimezone = this.q360TimezoneToIANA(timezone);
            now = now.setZone(ianaTimezone);
        }
        return this.convertBackToOriginalType(now, returnType);
    }
    /**
    * Utility to convert a JS Date object to Luxon DateTime
    * */
    static fromJSDate(jsDate) {
        return DateTime.fromJSDate(jsDate);
    }
    /**
     * Utility to get a Luxon DateTime from the Epcoch
     * @param epoch
     * @param milliseconds
     */
    static fromEpoch(epoch, milliseconds = false) {
        return milliseconds ? DateTime.fromMillis(epoch) : DateTime.fromSeconds(epoch);
    }
    /**
     * Formats the given date into a string using the provided format string.
     *
     * @param {DateTime | Date | string} date - The date to be formatted.
     * @param {string} formatString - The format string used to format the date.
     * @returns {string} The formatted date string according to the specified format.
     */
    static formatDate(date, formatString = this.dateFormat) {
        if (!date) {
            return '';
        }
        if (formatString === 'display') {
            formatString = ENV('dateFormat');
        }
        const dateTime = this.toDateTime(date);
        return dateTime.toFormat(formatString);
    }
    static formatDateWithCaptions(date, formatString = this.dateFormat) {
        if (!date) {
            return '';
        }
        if (formatString === 'display') {
            formatString = ENV('dateFormat');
        }
        const dateTime = this.toDateTime(date);
        // Replace EEEE (weekday long)
        formatString = formatString.replace(/EEEE/g, `'${this.getDayName(dateTime)}'`);
        // Replace MMMM (month long)
        formatString = formatString.replace(/MMMM/g, `'${this.getMonthName(dateTime)}'`);
        return dateTime.toFormat(formatString);
    }
    /**
     * Formats the given date into a string using the provided format string. Optionally converts the timezone.
     *
     * @param {DateTime | Date | string} date The input date in the form of DateTime, Date object, or a string
     * @param {string} formatString The format string specifying how the date should be formatted
     * @param {string} sourceTZ The source timezone of the input date (default: 'SYSTEM')
     * @param {string} destinationTZ The destination timezone for formatting the date (default: 'SYSTEM')
     * @return {string} Returns the formatted date-time string based on the provided formatString and timezones
     */
    static formatDateTime(date, formatString = this.dateTimeFormat, sourceTZ = 'SYSTEM', destinationTZ = 'SYSTEM') {
        if (!date) {
            return '';
        }
        if (formatString === 'display') {
            formatString = ENV('datetimeFormat');
        }
        const sourceDate = this.toDateTime(date, sourceTZ);
        const destination = this.q360TimezoneToIANA(destinationTZ);
        const destinationDate = sourceDate.setZone(destination);
        return destinationDate.toFormat(formatString);
    }
    /**
     * Converts a date/time from one timezone to another.
     *
     * @param {DateTime | string | Date} date The date/time to convert. Can be a Luxon DateTime object, a string in ISO format, or a JavaScript Date object.
     * @param {string} sourceTimezone The timezone of the original date/time.
     * @param {string} destinationTimezone The timezone to convert the date/time to.
     * @return {DateTime | string | Date} The converted date/time in the destination timezone with the same type as the input date/time.
     */
    static convertTimezone(date, sourceTimezone, destinationTimezone, original) {
        if (!date) {
            return '';
        }
        const dateTime = this.toDateTime(date, sourceTimezone);
        const source = this.q360TimezoneToIANA(sourceTimezone);
        const destination = this.q360TimezoneToIANA(destinationTimezone);
        if (source === destination) {
            if (original) {
                return this.convertBackToOriginalType(dateTime, original);
            }
            return date;
        }
        const convertedDate = dateTime.setZone(destination);
        return this.convertBackToOriginalType(convertedDate, original || date);
    }
    /**
    * Utility for Timezone Translation, to translate special keywords like SYSTEM and USER to their respective Q360 timezone values.
    * */
    static determineTimeZone(q360TimezoneName) {
        if (q360TimezoneName === 'SYSTEM' || !q360TimezoneName) {
            return CONFIG('SERVERTIMEZONE', 1);
        }
        else if (q360TimezoneName === 'USER') {
            return ENV('userTimezone');
        }
        else {
            return q360TimezoneName;
        }
    }
    /**
    * Utility to convert any q360 timezone name to an IANA standard timezone so Luxon can do timezone conversion
    * */
    static q360TimezoneToIANA(q360TimezoneName) {
        const sqlZone = ENV('timezoneMapping')[this.determineTimeZone(q360TimezoneName)];
        if (!this.SqlToIANAMapping[sqlZone]) {
            console.warn(`Unhandled Timezone!! Q360Timezone: ${q360TimezoneName} SQL Timezone: ${sqlZone}`);
        }
        return this.SqlToIANAMapping[sqlZone];
    }
    /**
     * Gets the timezone offset from UTC in the format ±HH:mm (ISO 8601 format)
     *
     * @param {string} [timezone='SYSTEM'] The timezone to get the offset for (defaults to system timezone)
     * @param {DateTime | string | Date} [date] Optional date to use for offset calculation (defaults to current time)
     * @returns {string} The timezone offset in ±HH:mm format (e.g., '+05:30', '-08:00', '+00:00')
     */
    static getTimezoneOffset(timezone = 'SYSTEM', date) {
        let dateTime;
        if (date) {
            dateTime = this.toDateTime(date, timezone);
        }
        else {
            const ianaTimezone = this.q360TimezoneToIANA(timezone);
            dateTime = DateTime.now().setZone(ianaTimezone);
        }
        return dateTime.toFormat('ZZ');
    }
    // MISCELLANEOUS DATE HELPER FUNCTIONS
    /**
     * Function to get locale-based datetime format (this is influenced by chrome > settings > language)
     */
    static getLocaleBasedDatetimeFormat() {
        const formatter = new Intl.DateTimeFormat(navigator.language, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
        const parts = formatter.formatToParts(new Date());
        return this.extractPatternFromDateParts(parts, true, (!formatter.resolvedOptions().hour12));
    }
    /**
     * Helper function to extract the pattern from date parts
     * @param parts
     * @param isDatetime
     * @param format24Hours
     */
    static extractPatternFromDateParts(parts, isDatetime = false, format24Hours = false) {
        let pattern = '';
        parts.forEach(part => {
            switch (part.type) {
                case 'year':
                    pattern += 'yyyy';
                    break;
                case 'month':
                    pattern += 'MM';
                    break;
                case 'day':
                    pattern += 'dd';
                    break;
                case 'hour':
                    if (isDatetime) {
                        pattern += format24Hours ? 'HH' : 'hh';
                    } // Use 12-hour format for datetime
                    break;
                case 'minute':
                    if (isDatetime) {
                        pattern += 'mm';
                    }
                    break;
                case 'second':
                    if (isDatetime) {
                        pattern += 'ss';
                    }
                    break;
                case 'dayPeriod':
                    if (isDatetime && !format24Hours) {
                        pattern += 'a';
                    } // Add AM/PM at the end
                    break;
                case 'literal':
                    pattern += part.value.replace(',', '') /*.trim()*/; // Remove commas or extra spaces
                    break;
            }
        });
        return pattern;
    }
    /**
     *
     * @param {string} sourceTimezone The timezone of the source location in IANA format
     * @param {string} destinationTimezone The timezone of the destination location in IANA format
     * @param {string | DateTime} [date] Optional. The date for which the offset needs to be calculated. If not provided, the current date and time will be used.
     * @return {number} The time difference in hours between the source and destination timezones
     */
    static calculateOffset(sourceTimezone, destinationTimezone, date) {
        if (!date) {
            date = this.now();
        }
        const source = this.q360TimezoneToIANA(sourceTimezone);
        const destination = this.q360TimezoneToIANA(destinationTimezone);
        let dateTime;
        if (typeof date === 'string') {
            dateTime = this.parseDateTime(date, source);
        }
        else {
            dateTime = date.setZone(source);
        }
        const convertedDate = dateTime.setZone(destination, { keepLocalTime: true });
        const offset = dateTime.diff(convertedDate, 'minutes').minutes / 60;
        return offset;
    }
    static timeStringToFloat(time, sourceTZ, destinationTZ) {
        let formattedTime = time;
        if (time.toLowerCase().includes('am') || time.toLowerCase().includes('pm')) {
            formattedTime = this.convert12HourTimeStringTo24Hour(time, sourceTZ, destinationTZ);
        }
        else {
            const dDate = DateTime.now().toFormat('yyyy-MM-dd');
            formattedTime = this.formatDateTime(`${dDate} ${time}:00.000`, 'HH:mm', sourceTZ, destinationTZ);
        }
        const [hours, minutes] = formattedTime.split(':').map(part => parseInt(part, 10));
        return hours + (minutes / 60);
    }
    static getPartFromDateTimeFormat(dateTimeFormat, part) {
        const timeIndex = Math.min(dateTimeFormat.indexOf('H') >= 0 ? dateTimeFormat.indexOf('H') : Infinity, dateTimeFormat.indexOf('h') >= 0 ? dateTimeFormat.indexOf('h') : Infinity);
        // If no time-related characters are found, return the entire string for 'date'
        if (timeIndex === Infinity) {
            return part === 'date' ? dateTimeFormat.trim() : '';
        }
        return part === 'date' ? dateTimeFormat.slice(0, timeIndex).trim() : dateTimeFormat.slice(timeIndex).trim();
    }
    static convert12HourTimeStringTo24Hour(time, sourceTZ, destinationTZ) {
        const [timePart, period] = time.split(/(am|pm)/i);
        let [hours, minutes] = timePart.split(':').map(part => parseInt(part, 10));
        if (period.toLowerCase() === 'pm' && hours !== 12) {
            hours += 12;
        }
        else if (period.toLowerCase() === 'am' && hours === 12) {
            hours = 0;
        }
        const dDate = DateTime.now().toFormat('yyyy-MM-dd');
        return this.formatDateTime(`${dDate} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00.000`, 'HH:mm', sourceTZ, destinationTZ);
    }
    /**
     * Returns true if the given value can be parsed/converted into a valid Luxon DateTime.
     *
     * @param {DateTime | string | Date} date – the value to test
     * @param {string} [sourceTZ='SYSTEM'] – the timezone to assume when parsing strings or JS Dates
     */
    static isValid(date, sourceTZ = 'SYSTEM') {
        if (!date) {
            return false;
        }
        if (date instanceof DateTime) {
            return date.isValid;
        }
        // toDateTime will call parseDateTime() for strings and fromJSDate() for Date objects
        const dt = this.toDateTime(date, sourceTZ);
        return dt.isValid;
    }
}
// not to be used for display purposes. More for like getting a variable to submit with an ajax call 
// ie. var currentDateTime = DateTimeManager.now<DateTime>().toFormat(DateTimeManager.dateTimeFormat);
DateTimeManager.dateTimeFormat = ENV('datetimeStorageFormat'); //'yyyy-MM-dd HH:mm:ss';
DateTimeManager.dateFormat = ENV('dateStorageFormat'); //'yyyy-MM-dd';
DateTimeManager.timeFormat = ENV('timeStorageFormat'); //'HH:mm:ss.SSS';
/**
* A Mapping of all known SQL timezones to their respective IANA standard timezone.
* */
DateTimeManager.SqlToIANAMapping = {
    'dateline standard time': 'Pacific/Wake',
    'samoa standard time': 'Pacific/Pago_Pago',
    'utc-11': 'Pacific/Pago_Pago',
    'aleutian standard time': 'America/Adak',
    'hawaiian standard time': 'Pacific/Honolulu',
    'marquesas standard time': 'Pacific/Marquesas',
    'alaskan standard time': 'America/Anchorage',
    'utc-09': 'Pacific/Gambier',
    'yukon standard time': 'America/Whitehorse',
    'pacific standard time (mexico)': 'America/Los_Angeles',
    'utc-08': 'Pacific/Pitcairn',
    'pacific standard time': 'America/Los_Angeles',
    'us mountain standard time': 'America/Phoenix',
    'mountain standard time (mexico)': 'America/Denver',
    'mountain standard time': 'America/Denver',
    'eastern standard time (mexico)': 'America/New_York',
    'central america standard time': 'America/Managua',
    'central standard time': 'America/Chicago',
    'easter island standard time': 'Pacific/Easter',
    'central standard time (mexico)': 'America/Mexico_City',
    'canada central standard time': 'America/Regina',
    'sa pacific standard time': 'America/Bogota',
    'eastern standard time': 'America/New_York',
    'haiti standard time': 'America/Port-au-Prince',
    'cuba standard time': 'America/Havana',
    'us eastern standard time': 'America/Indiana/Indianapolis',
    'turks and caicos standard time': 'America/Grand_Turk',
    'venezuela standard time': 'America/Caracas',
    'magallanes standard time': 'Pacific/Easter',
    'paraguay standard time': 'America/Asuncion',
    'atlantic standard time': 'America/Halifax',
    'central brazilian standard time': 'America/Belem',
    'sa western standard time': 'America/La_Paz',
    'pacific sa standard time': 'America/Santiago',
    'newfoundland standard time': 'America/St_Johns',
    'tocantins standard time': 'America/Belem',
    'e. south america standard time': 'America/Sao_Paulo',
    'sa eastern standard time': 'America/Argentina/Buenos_Aires',
    'argentina standard time': 'America/Argentina/Buenos_Aires',
    'greenland standard time': 'America/Godthab',
    'montevideo standard time': 'America/Montevideo',
    'saint pierre standard time': 'America/Miquelon',
    'bahia standard time': 'America/Bahia',
    'utc-02': 'America/Noronha',
    'mid-atlantic standard time': 'America/Noronha',
    'azores standard time': 'Atlantic/Azores',
    'cape verde standard time': 'Atlantic/Cape_Verde',
    'utc': 'Etc/UTC',
    'gmt standard time': 'Europe/London',
    'greenwich standard time': 'Europe/London',
    'morocco standard time': 'Africa/Casablanca',
    'w. europe standard time': 'Europe/Lisbon',
    'central europe standard time': 'Europe/London',
    'romance standard time': 'Europe/Paris',
    'central european standard time': 'Europe/London',
    'w. central africa standard time': 'Africa/Lagos',
    'libya standard time': 'Africa/Tripoli',
    'namibia standard time': 'Africa/Windhoek',
    'jordan standard time': 'Asia/Amman',
    'gtb standard time': 'Europe/Istanbul',
    'middle east standard time': 'Asia/Karachi',
    'egypt standard time': 'Africa/Cairo',
    'e. europe standard time': 'Europe/Bucharest',
    'syria standard time': 'Asia/Damascus',
    'west bank standard time': 'Asia/Jerusalem',
    'south africa standard time': 'Africa/Johannesburg',
    'fle standard time': 'Europe/Mariehamn',
    'israel standard time': 'Asia/Jerusalem',
    'south sudan standard time': 'Africa/Juba',
    'kaliningrad standard time': 'Europe/Kaliningrad',
    'sudan standard time': 'Africa/Khartoum',
    'turkey standard time': 'Europe/Istanbul',
    'belarus standard time': 'Europe/Minsk',
    'arabic standard time': 'Asia/Baghdad',
    'arab standard time': 'Asia/Baghdad',
    'russian standard time': 'Europe/Moscow',
    'e. africa standard time': 'Africa/Nairobi',
    'astrakhan standard time': 'Europe/Astrakhan',
    'russia time zone 3': 'Europe/Moscow',
    'saratov standard time': 'Europe/Saratov',
    'volgograd standard time': 'Europe/Volgograd',
    'iran standard time': 'Asia/Tehran',
    'arabian standard time': 'Asia/Baghdad',
    'azerbaijan standard time': 'Asia/Tbilisi',
    'mauritius standard time': 'Indian/Mauritius',
    'georgian standard time': 'Asia/Tbilisi',
    'caucasus standard time': 'Asia/Tbilisi',
    'afghanistan standard time': 'Asia/Kabul',
    'west asia standard time': 'Asia/Aqtau',
    'ekaterinburg standard time': 'Asia/Yekaterinburg',
    'pakistan standard time': 'Asia/Karachi',
    'qyzylorda standard time': 'Asia/Qyzylorda',
    'india standard time': 'Asia/Colombo',
    'sri lanka standard time': 'Asia/Colombo',
    'nepal standard time': 'Asia/Katmandu',
    'central asia standard time': 'Asia/Dhaka',
    'bangladesh standard time': 'Asia/Dhaka',
    'omsk standard time': 'Asia/Omsk',
    'altai standard time': 'Europe/Moscow',
    'n. central asia standard time': 'Asia/Novosibirsk',
    'tomsk standard time': 'Asia/Tomsk',
    'myanmar standard time': 'Asia/Yangon',
    'se asia standard time': 'Asia/Bangkok',
    'w. mongolia standard time': 'Asia/Choibalsan',
    'north asia standard time': 'Asia/Krasnoyarsk',
    'china standard time': 'Asia/Shanghai',
    'north asia east standard time': 'Asia/Irkutsk',
    'singapore standard time': 'Asia/Singapore',
    'w. australia standard time': 'Australia/Perth',
    'taipei standard time': 'Asia/Taipei',
    'ulaanbaatar standard time': 'Asia/Ulaanbaatar',
    'transbaikal standard time': 'Asia/Yakutsk',
    'north korea standard time': 'Asia/Pyongyang',
    'aus central w. standard time': 'Australia/Eucla',
    'tokyo standard time': 'Asia/Tokyo',
    'korea standard time': 'Asia/Seoul',
    'yakutsk standard time': 'Asia/Yakutsk',
    'cen. australia standard time': 'Australia/Adelaide',
    'aus central standard time': 'Australia/Darwin',
    'e. australia standard time': 'Australia/Brisbane',
    'aus eastern standard time': 'Australia/Brisbane',
    'west pacific standard time': 'Pacific/Port_Moresby',
    'tasmania standard time': 'Australia/Hobart',
    'vladivostok standard time': 'Asia/Vladivostok',
    'bougainville standard time': 'Pacific/Bougainville',
    'magadan standard time': 'Asia/Magadan',
    'sakhalin standard time': 'Asia/Sakhalin',
    'lord howe standard time': 'Australia/Lord_Howe',
    'russia time zone 10': 'Europe/Moscow',
    'norfolk standard time': 'Pacific/Norfolk',
    'central pacific standard time': 'Pacific/Guadalcanal',
    'russia time zone 11': 'Europe/Moscow',
    'new zealand standard time': 'Pacific/Auckland',
    'utc+12': 'Pacific/Fiji',
    'fiji standard time': 'Pacific/Fiji',
    'kamchatka standard time': 'Asia/Kamchatka',
    'chatham islands standard time': 'Pacific/Chatham',
    'utc+13': 'Pacific/Tongatapu',
    'tonga standard time': 'Pacific/Tongatapu',
    'line islands standard time': 'Pacific/Kanton',
    'tzone://microsoft/utc': 'Etc/UTC'
};
/*private _date: Date;
private locale: string = ENV('locale');

constructor(isoString: string) {
    this._date = new Date(isoString);
}*/
/*// If the string lacks time information, treat it as UTC midnight.
        if (!isoString.includes(' ') && !isoString.includes('T')) {
            isoString += 'T00:00:00Z';
        } else if (isoString.includes(' ')) {
            // Replace space with 'T' for partial times and append 'Z' for UTC
            isoString = isoString.replace(' ', 'T') + 'Z';
        }
        
        return new DateTimeManager(isoString);*/
/*
    toFormat(formatString: string, IANA_Timezone: string = 'Etc/UTC'): string {
        const tokens: { [key: string]: () => string } = {
            // Year
            'yyyy': () => this._date.getFullYear().toString(),
            'yy': () => this._date.getFullYear().toString().slice(-2),
            // Month
            'MMMM': () => new Intl.DateTimeFormat(this.locale, { month: 'long' }).format(this._date),
            'MMM': () => new Intl.DateTimeFormat(this.locale, { month: 'short' }).format(this._date),
            'MM': () => ('0' + (this._date.getMonth() + 1)).slice(-2),
            'M': () => (this._date.getMonth() + 1).toString(),
            // Day
            'dd': () => ('0' + this._date.getDate()).slice(-2),
            'd': () => this._date.getDate().toString(),
            // Hour
            'HH': () => ('0' + this._date.getHours()).slice(-2),
            'H': () => this._date.getHours().toString(),
            'hh': () => ('0' + this._date.getHours() % 12 || '12').slice(-2),
            'h': () => (this._date.getHours() % 12 || 12).toString(),
            // Minute
            'mm': () => ('0' + this._date.getMinutes()).slice(-2),
            'm': () => this._date.getMinutes().toString(),
            // Second
            'ss': () => ('0' + this._date.getSeconds()).slice(-2),
            's': () => this._date.getSeconds().toString(),
        };
        
        return formatString.replace(/\b(yyyy|yy|MMMM|MMM|MM|M|dd|d|HH|H|hh|h|mm|m|ss|s)\b/g, match => tokens[match]());
    }*/ 
